import React, { useState, useEffect, useRef } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import './VariantSelect.css';

const COLOR_MAP = {
  black: '#000000', white: '#ffffff', navy: '#000080', maroon: '#800000',
  red: '#dc2626', blue: '#2563eb', pink: '#ec4899', green: '#16a34a',
  grey: '#6b7280', gray: '#6b7280', beige: '#d4c4a8', brown: '#92400e',
  yellow: '#eab308', orange: '#ea580c', purple: '#7c3aed', 'sky blue': '#0ea5e9',
  cream: '#fef9c3', charcoal: '#374151', gold: '#d97706', silver: '#9ca3af',
  olive: '#84cc16', teal: '#0d9488', coral: '#f97316', lavender: '#a78bfa',
  peach: '#fec89a', mustard: '#ca8a04', burgundy: '#881337', turquoise: '#2dd4bf',
  wine: '#722f37', chocolate: '#78350f', mint: '#6ee7b7'
};

const getHexForColor = (name) => {
  if (!name) return '#94a3b8';
  return COLOR_MAP[String(name).toLowerCase()] || '#94a3b8';
};

/** Variant display label without "Default" (e.g. "Default / XL" → "XL") */
const variantDisplayLabel = (v) => {
  if (!v) return 'Variant';
  const parts = [v.color, v.size].filter(Boolean).filter(x => String(x).toLowerCase() !== 'default');
  return parts.length ? parts.join(' / ') : 'Variant';
};

/**
 * Searchable variant picker for work orders - shows color swatch, color name, and available guz.
 */
const VariantSelect = ({
  variants = [],
  variantInventory = {},
  value,
  onChange,
  placeholder = 'Select variant',
  disabled,
  excludeVariantIds = [],
  showAllVariants = false  // when true, show all variants with stock (including 0)
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  const availableVariants = variants.filter(v => {
    if (excludeVariantIds.includes(v.id)) return false;
    if (showAllVariants) return true;
    const qty = variantInventory[v.id] ?? 0;
    return qty > 0;
  });

  const filtered = availableVariants.filter(v => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return true;
    const color = (v.color || '').toLowerCase();
    return color.includes(q);
  });

  const selectedVariant = variants.find(v => v.id.toString() === String(value));
  const displayHex = getHexForColor(selectedVariant?.color);
  const selectedQty = selectedVariant ? (variantInventory[selectedVariant.id] ?? 0) : 0;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  return (
    <div className="variant-select" ref={containerRef}>
      <button
        type="button"
        className={`variant-select-trigger ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
      >
        <span className="variant-select-value">
          {value && selectedVariant ? (
            <>
              <span className="variant-swatch" style={{ background: displayHex }} />
              <span className="variant-label">
                {variantDisplayLabel(selectedVariant)} · {selectedQty.toFixed(2)} guz available
              </span>
            </>
          ) : (
            <span className="variant-placeholder">{placeholder}</span>
          )}
        </span>
        <HiChevronDown className="variant-select-chevron" />
      </button>

      {open && (
        <div className="variant-select-dropdown">
          <input
            type="text"
            className="variant-select-search"
            placeholder="Search by color..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="variant-select-list">
            {filtered.length === 0 ? (
              <div className="variant-select-empty">
                {variants.length === 0 ? 'No variants' : 'No matching colors'}
              </div>
            ) : (
              filtered.map(v => {
                const qty = variantInventory[v.id] ?? 0;
                const hex = getHexForColor(v.color);
                const isSelected = v.id.toString() === String(value);
                return (
                  <button
                    key={v.id}
                    type="button"
                    className={`variant-select-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      onChange(v.id);
                      setOpen(false);
                    }}
                  >
                    <span className="variant-swatch" style={{ background: hex }} />
                    <span className="variant-option-label">
                      {variantDisplayLabel(v)} – {qty.toFixed(2)} guz available
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantSelect;
